import type { AccountScanSummaryResponse, ApplyClusterRequest, ApplyClusterResponse, BatchApplyRequest, BatchReassignTrackRequest, BatchReassignTrackResponse, ClassificationRejectSuggestionRequest, EmailConsentPurpose, EmailConsentSource, FeedbackOfferCheckoutResponse, GetSendersByCategoryResponse, GetSendersResponse, ListClassificationSuggestionsResponse, ListMcpConnectionsResponse, LogConsentRequest, LogConsentResponse, RevokeMcpConnectionResponse, SubmitFeedbackRequest, SubmitFeedbackResponse, SuggestionDebugPayload, SuggestionStatsResponse, UpdateEmailPreferenceRequest, UserAttributionPayload } from '@purebox/shared';
import { appContract } from '@purebox/shared';
import { initClient } from '@ts-rest/core';
import { getCopy } from './copy';
import { decodeEmailTextFields } from './decode-email-text';
import { Logger } from './logger';
import { getBrowserAccessToken } from './supabase/session-client';
import { CLIENT_VERSION_INFO } from './version-client';

const copy = (key: string) => getCopy('/_shared', key);

const RAW_API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3001';

// In the browser during development, use a relative (same-origin) base URL so
// API calls go through this Next.js origin and get proxied to the backend by the
// rewrites in next.config.mjs. This avoids saturating a second forwarded port in
// dev containers. Server-side rendering and production keep the absolute URL.
const API_BASE_URL =
  typeof window !== 'undefined' && process.env.NODE_ENV !== 'production' ? '' : RAW_API_BASE_URL;

const toNumberString = (value?: number) => (value !== undefined ? String(value) : undefined);

const limitOffsetQuery = (params?: { limit?: number; offset?: number }) => ({
  limit: toNumberString(params?.limit),
  offset: toNumberString(params?.offset),
});

let loginRedirectInFlight = false;

function isAuthRoutePath(pathname: string): boolean {
  return pathname === '/auth' || pathname.startsWith('/auth/');
}

function shouldSuppressLoginRedirect(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return isAuthRoutePath(window.location.pathname);
}

export class ApiClientError extends Error {
  constructor(
    public userMessage: string,
    public technicalDetails: string,
    public statusCode?: number,
    public code?: string
  ) {
    super(userMessage);
    this.name = 'ApiClientError';
  }
}

type GmailStatusListener = (event: { error: ApiClientError; retry?: () => Promise<unknown> }) => void;

const gmailStatusListeners = new Set<GmailStatusListener>();

export function subscribeGmailStatusListener(listener: GmailStatusListener): () => void {
  gmailStatusListeners.add(listener);
  return () => gmailStatusListeners.delete(listener);
}

function isGmailStatusError(error: ApiClientError): boolean {
  return error.code === 'GMAIL_TOKEN_EXPIRED' ||
    error.code === 'GMAIL_TOKEN_MISSING' ||
    error.code === 'GMAIL_SERVICE_UNAVAILABLE';
}

function notifyGmailStatusListeners(error: ApiClientError, retry?: () => Promise<unknown>): void {
  if (!isGmailStatusError(error)) return;

  for (const listener of gmailStatusListeners) {
    listener({ error, retry });
  }
}

function getNetworkErrorMessage(error: Error): { user: string; technical: string } {
  const errorMsg = error.message.toLowerCase();

  if (errorMsg.includes('failed to fetch')) {
    return {
      user: copy('apiError.network.unreachable'),
      technical: `Network fetch failed. API_BASE_URL=${API_BASE_URL}. Error: ${error.message}`,
    };
  }

  if (errorMsg.includes('net::err_blocked_by_client')) {
    return {
      user: copy('apiError.network.blocked'),
      technical: `Request blocked by client extension. Endpoint: ${API_BASE_URL}`,
    };
  }

  if (errorMsg.includes('err_name_not_resolved') || errorMsg.includes('enotfound')) {
    return {
      user: copy('apiError.network.notFound'),
      technical: `DNS resolution failed for ${API_BASE_URL}. Error: ${error.message}`,
    };
  }

  if (errorMsg.includes('err_connection_refused')) {
    return {
      user: copy('apiError.network.refused'),
      technical: `Connection refused to ${API_BASE_URL}. Server may be offline.`,
    };
  }

  if (errorMsg.includes('timeout')) {
    return {
      user: copy('apiError.network.timeout'),
      technical: `Request timeout to ${API_BASE_URL}. Error: ${error.message}`,
    };
  }

  return {
    user: copy('apiError.network.generic'),
    technical: `Network error: ${error.message}`,
  };
}

async function getAccessToken(): Promise<string | null> {
  return getBrowserAccessToken();
}

type TsRestResponse<T> = { status: number; body: T };

type AnyErrorBody = { message?: string; error?: string; code?: string };

function mapToApiError(res: TsRestResponse<AnyErrorBody>): ApiClientError {
  // ts-rest wraps thrown fetch errors (e.g., backend down, DNS failure) into
  // a response whose body is the original Error instance. Surface a friendly
  // "backend unreachable" message instead of a generic "Request failed".
  if (res.body instanceof Error) {
    const { user, technical } = getNetworkErrorMessage(res.body);
    return new ApiClientError(user, technical, res.status, 'NETWORK_ERROR');
  }

  const message = res.body?.message || res.body?.error || 'Request failed';
  const code = res.body?.code || 'UNKNOWN';

  let userMessage: string;
  if (code === 'GMAIL_TOKEN_EXPIRED' || code === 'GMAIL_TOKEN_MISSING' || code === 'GMAIL_SERVICE_UNAVAILABLE') {
    userMessage = message;
  } else if (res.status === 401) {
    userMessage = copy('apiError.http.unauthorized');
  } else if (res.status === 404) {
    userMessage = copy('apiError.http.notFound');
  } else if (res.status >= 500) {
    userMessage = copy('apiError.http.serverError');
  } else {
    userMessage = message;
  }

  return new ApiClientError(userMessage, `HTTP ${res.status}: ${message} (${code})`, res.status, code);
}

function isSupabaseAuthError(error: ApiClientError): boolean {
  return (
    error.statusCode === 401 &&
    (error.code === 'INVALID_SUPABASE_TOKEN' || error.code === 'UNAUTHORIZED')
  );
}

function redirectToLogin(): never {
  if (typeof window !== 'undefined') {
    if (loginRedirectInFlight) {
      return new Promise(() => {}) as never;
    }

    loginRedirectInFlight = true;

    const currentUrl = window.location.pathname + window.location.search;
    if (!currentUrl.startsWith('/auth/login')) {
      // Only store relative app paths; validated on consumption via sanitizeRedirectPath
      sessionStorage.setItem('post_login_redirect', currentUrl);
    }
    
    Logger.info('Redirecting to login due to authentication error', { 
      currentUrl,
      timestamp: new Date().toISOString() 
    });
    
    window.location.assign('/auth/login');
  }

  return new Promise(() => {}) as never;
}

async function unwrap<T = any>(promise: Promise<TsRestResponse<T>>, retry?: () => Promise<unknown>): Promise<T> {
  try {
    const res = await promise;

    if (res.status === 401) {
      const errorBody = res.body as AnyErrorBody;
      const code = errorBody?.code || 'UNKNOWN';

      if (code === 'INVALID_SUPABASE_TOKEN' || code === 'UNAUTHORIZED') {
        if (shouldSuppressLoginRedirect()) {
          throw mapToApiError(res as TsRestResponse<AnyErrorBody>);
        }

        return redirectToLogin();
      }
    }

    if (res.status >= 400) {
      const error = mapToApiError(res as TsRestResponse<AnyErrorBody>);
      notifyGmailStatusListeners(error, retry);
      throw error;
    }

    return decodeEmailTextFields(res.body);
  } catch (error) {
    if (error instanceof ApiClientError) {
      if (isSupabaseAuthError(error)) {
        if (shouldSuppressLoginRedirect()) {
          throw error;
        }

        return redirectToLogin();
      }
      notifyGmailStatusListeners(error, retry);
      throw error;
    }

    if (error instanceof Error) {
      const { user, technical } = getNetworkErrorMessage(error);
      throw new ApiClientError(user, technical);
    }

    throw new ApiClientError(copy('apiError.unknown'), `Unknown error: ${String(error)}`);
  }
}

async function unwrapAuthed<T = any>(call: (headers: Awaited<ReturnType<typeof getAuthHeaders>>) => Promise<TsRestResponse<T>>): Promise<T> {
  const execute = async () => call(await getAuthHeaders());
  return unwrap<T>(execute(), () => unwrapAuthed<T>(call));
}

async function getAuthHeaders() {
  const token = await getAccessToken();

  // Do not send empty Authorization headers. If the browser session has not
  // hydrated yet (or the user is signed out), firing the request anyway makes
  // the backend log a misleading INVALID_SUPABASE_TOKEN/"No token provided"
  // error for what is really a client-side race. Throw a synthetic 401 that
  // unwrap() translates into its normal unauthenticated redirect flow.
  if (!token) {
    throw new ApiClientError(
      copy('apiError.http.unauthorized'),
      'HTTP 401: Missing browser Supabase session (UNAUTHENTICATED_NO_SESSION)',
      401,
      'UNAUTHENTICATED_NO_SESSION',
    );
  }

  return {
    Authorization: `Bearer ${token}`,
    'X-FE-Version': CLIENT_VERSION_INFO.gitSha,
  };
}

const client: any = initClient(appContract, {
  baseUrl: API_BASE_URL,
});
export const apiClient = {
  consent: {
    logCookies: async (payload: LogConsentRequest) =>
      unwrap<LogConsentResponse>(client.consent.logConsent({ body: payload })),
  },

  feedback: {
    // First-party product feedback (#707). Requires auth but no analytics
    // consent - this path must work for users who declined cookies.
    submit: async (payload: SubmitFeedbackRequest) =>
      unwrapAuthed<SubmitFeedbackResponse>((headers) =>
        client.feedback.submitFeedback({ body: payload, extraHeaders: headers })),
  },

  manage: {
    listSenders: async (params?: { limit?: number; offset?: number; search?: string; sortBy?: 'count' | 'size' | 'sender'; sortDir?: 'asc' | 'desc' }) =>
      unwrapAuthed<GetSendersResponse>((headers) => client.manage.listSenders({ query: {
        ...limitOffsetQuery(params),
        search: params?.search,
        sortBy: params?.sortBy,
        sortDir: params?.sortDir,
      }, extraHeaders: headers })),

    listCategories: async (params?: { limit?: number; offset?: number }) =>
      unwrap<any>(client.manage.listCategories({ query: limitOffsetQuery(params), extraHeaders: await getAuthHeaders() })),

    listSendersByCategory: async (category: string, params?: { limit?: number; offset?: number }) =>
      unwrap<GetSendersByCategoryResponse>(client.manage.listSendersByCategory({
        params: { category },
        query: { category, ...limitOffsetQuery(params) },
        extraHeaders: await getAuthHeaders(),
      })),

    listEmails: async (params: { senderEmail?: string; category?: string; limit?: number; offset?: number }) =>
      unwrapAuthed<any>((headers) => client.manage.listEmails({ query: {
        senderEmail: params.senderEmail,
        category: params.category,
        ...limitOffsetQuery(params),
      }, extraHeaders: headers })),

    labelAnalysis: async (params?: { limit?: number; offset?: number }) =>
      unwrap<any>(client.manage.labelAnalysis({ query: limitOffsetQuery(params), extraHeaders: await getAuthHeaders() })),

    consistentLabelSenders: async (params?: { limit?: number; offset?: number }) =>
      unwrap<any>(client.manage.consistentLabelSenders({ query: limitOffsetQuery(params), extraHeaders: await getAuthHeaders() })),
  },

  suggestions: {
    list: async (params?: { previewLimit?: number }) =>
      unwrap<any>(client.suggestions.list({ query: { previewLimit: toNumberString(params?.previewLimit) }, extraHeaders: await getAuthHeaders() })),

    preview: async (suggestionId: string, params?: { limit?: number }) =>
      unwrap<any>(client.suggestions.preview({
        params: { suggestionId },
        query: { limit: toNumberString(params?.limit) },
        extraHeaders: await getAuthHeaders(),
      })),

    apply: async (suggestionId: string, payload: any) =>
      unwrap<any>(client.suggestions.apply({ params: { suggestionId }, body: payload, extraHeaders: await getAuthHeaders() })),
  },

  labels: {
    apply: async (payload: { emailIds: string[]; label: string }) => 
      unwrapAuthed<any>((headers) => client.labels.apply({ body: payload, extraHeaders: headers })),
  },

  activity: {
    list: async (params?: { limit?: number; from?: string; to?: string; kind?: 'parent' | 'category' | 'custom'; source?: 'auto_applied' | 'manual' | 'all'; action?: 'archive' | 'trash' | 'all'; search?: string }) =>
      unwrap<any>(client.activity.list({ query: {
        limit: toNumberString(params?.limit),
        from: params?.from,
        to: params?.to,
        kind: params?.kind,
        source: params?.source,
        action: params?.action,
        search: params?.search,
      }, extraHeaders: await getAuthHeaders() })),
    detail: async (id: string) => unwrap<any>(client.activity.detail({ params: { id }, extraHeaders: await getAuthHeaders() })),
    getAutoAppliedSummary: async (window: '7d' | '30d' = '7d') =>
      unwrap<any>(client.activity.getAutoAppliedSummary({ query: { window }, extraHeaders: await getAuthHeaders() })),
    affectedEmails: async (id: string, params?: { limit?: number; offset?: number }) =>
      unwrap<any>(client.activity.affectedEmails({
        params: { id },
        query: {
          limit: toNumberString(params?.limit),
          offset: toNumberString(params?.offset),
        },
        extraHeaders: await getAuthHeaders(),
      })),
  },

  classify: {
    listEmails: async (params?: { limit?: number; source?: 'db' | 'gmail' }) =>
      unwrapAuthed<any>((headers) => client.classify.listEmails({ query: {
        limit: params?.limit,
        source: params?.source,
      }, extraHeaders: headers })),

    generateSuggestion: async (payload: { message_id: string; force_llm?: boolean; min_confidence?: number }) =>
      unwrapAuthed<any>((headers) => client.classify.generateSuggestion({ body: payload, extraHeaders: headers })),

    listSuggestions: async (params?: { status?: 'pending' | 'applied' | 'rejected'; track?: 'Attention' | 'Archive' | 'Trash'; scanJobId?: string; label?: string; search?: string; limit?: number; offset?: number; confidenceFloor?: number }) =>
      unwrapAuthed<ListClassificationSuggestionsResponse>((headers) => client.classify.listSuggestions({ query: {
        status: params?.status,
        track: params?.track,
        scanJobId: params?.scanJobId,
        // (#476) Filter to a single category within a track. Pass the
        // 'Uncategorized' sentinel to select rows with no labels.
        label: params?.label,
        search: params?.search,
        limit: params?.limit,
        offset: params?.offset,
        // (#296) Single-cohort floor. Server enforces >=0.85; the slider in
        // the Cleanup ribbon is the only producer of values >0.85.
        confidenceFloor: params?.confidenceFloor,
      }, extraHeaders: headers })),

    getSuggestionStats: async (params?: {
      status?: 'pending' | 'applied' | 'rejected';
      track?: 'Attention' | 'Archive' | 'Trash';
      scanJobId?: string;
      label?: string;
      search?: string;
      confidenceFloor?: number;
    }) =>
      unwrapAuthed<SuggestionStatsResponse>((headers) =>
        client.classify.getSuggestionStats({
          query: {
            status: params?.status,
            track: params?.track,
            scanJobId: params?.scanJobId,
            label: params?.label,
            search: params?.search,
            confidenceFloor: params?.confidenceFloor,
          },
          extraHeaders: headers,
        }),
      ),

    getSuggestion: async (suggestionId: string) =>
      unwrapAuthed<any>((headers) => client.classify.getSuggestion({ params: { id: suggestionId }, extraHeaders: headers })),

    // Dev-only. Full classification replay payload (raw headers, sender_insights,
    // llm_metadata) behind the Cleanup detail panel's "copy email details" button.
    // Returns 404 in production - callers must fall back gracefully.
    getSuggestionDebug: async (suggestionId: string): Promise<SuggestionDebugPayload> =>
      unwrapAuthed<SuggestionDebugPayload>((headers) => client.classify.getSuggestionDebug({ params: { id: suggestionId }, extraHeaders: headers })),

    applySuggestion: async (suggestionId: string, payload?: { action_overrides?: Record<string, boolean> }) =>
      unwrapAuthed<any>((headers) => client.classify.applySuggestion({ params: { id: suggestionId }, body: payload, extraHeaders: headers })),

    // (#350) Bulk-apply a small cluster of pending suggestions sharing a
    // sender or rationale in one request. Per-id failures are surfaced
    // in `results` and do NOT abort the batch.
    applyCluster: async (payload: ApplyClusterRequest): Promise<ApplyClusterResponse> =>
      unwrapAuthed<ApplyClusterResponse>((headers) => client.classify.applyCluster({ body: payload, extraHeaders: headers })),

    rejectSuggestion: async (suggestionId: string, payload?: ClassificationRejectSuggestionRequest) =>
      unwrap<any>(client.classify.rejectSuggestion({ params: { id: suggestionId }, body: payload, extraHeaders: await getAuthHeaders() })),

    // (#296) Cleanup ribbon "Exclude" row action. Body is intentionally empty;
    // the path param fully identifies the row to exclude.
    excludeSuggestion: async (suggestionId: string) =>
      unwrap<any>(client.classify.excludeSuggestion({ params: { id: suggestionId }, body: {}, extraHeaders: await getAuthHeaders() })),

    reassignTrack: async (suggestionId: string, track: 'Attention' | 'Archive' | 'Trash') =>
      unwrap<any>(client.classify.reassignTrack({ params: { id: suggestionId }, body: { track }, extraHeaders: await getAuthHeaders() })),

    batchReassignTrack: async (payload: BatchReassignTrackRequest): Promise<BatchReassignTrackResponse> =>
      unwrapAuthed<BatchReassignTrackResponse>((headers) => client.classify.batchReassignTrack({
        body: payload,
        extraHeaders: headers,
      })),

    reanalyzeAllSuggestions: async () =>
      unwrapAuthed<any>((headers) => client.classify.reanalyzeAllSuggestions({ body: {}, extraHeaders: headers })),

    // Dev-only. Starts a background run for the latest persisted scan sample.
    // The HTTP contract remains reclassifyAllEmails; it 404s in production.
    reclassifyLatestSample: async () =>
      unwrapAuthed<any>((headers) => client.classify.reclassifyAllEmails({ body: {}, extraHeaders: headers })),

    reanalyzeAllSuggestionsViaGet: async () =>
      unwrapAuthed<any>((headers) => client.classify.reanalyzeAllSuggestionsGet({ extraHeaders: headers })),

    reanalyzeSuggestion: async (suggestionId: string) =>
      unwrapAuthed<any>((headers) => client.classify.reanalyzeSuggestion({ params: { id: suggestionId }, body: {}, extraHeaders: headers })),

    batchApply: async (payload: BatchApplyRequest) =>
      unwrapAuthed<any>((headers) => client.classify.batchApply({ body: payload as any, extraHeaders: headers })),

    getActiveApplyJob: async () =>
      unwrap<any>(client.classify.getActiveApplyJob({ extraHeaders: await getAuthHeaders() })),

    getApplyJob: async (job_id: string) =>
      unwrap<any>(client.classify.getApplyJob({ params: { id: job_id }, extraHeaders: await getAuthHeaders() })),

    recordFeedback: async (
      suggestionId: string,
      payload: {
        verdict: 'accepted' | 'rejected' | 'overridden'
        override_track?: 'Attention' | 'Archive' | 'Trash'
        reject_reason?: 'wrong_track' | 'wrong_labels' | 'not_useful' | 'other'
        reject_reason_text?: string
      },
    ) =>
      unwrap<any>(client.classify.recordFeedback({ params: { id: suggestionId }, body: payload, extraHeaders: await getAuthHeaders() })),
  },

  auth: {
    getAuthUrl: async () => unwrap<any>(client.auth.getAuthUrl({ extraHeaders: await getAuthHeaders() })),
    callback: async (query: { code: string; state: string }) => unwrap<any>(client.auth.callback({ query })),
    disconnect: async () => unwrap<any>(client.auth.disconnect({ body: {}, extraHeaders: await getAuthHeaders() })),
    getGmailOauthStatus: async () => unwrapAuthed<any>((headers) => client.auth.getGmailOauthStatus({ extraHeaders: headers })),
    recordTermsAcceptance: async (input: { method: 'email' | 'google' | 'reconsent'; clickwrap?: boolean }) =>
      unwrap<any>(
        client.auth.recordTermsAcceptance({
          body: { method: input.method, clickwrap: input.clickwrap ?? false },
          extraHeaders: await getAuthHeaders(),
        }),
      ),
    getTermsAcceptanceStatus: async () =>
      unwrap<any>(client.auth.getTermsAcceptanceStatus({ extraHeaders: await getAuthHeaders() })),
  },

  account: {
    delete: async (payload: { confirmText: string }) =>
      unwrap<any>(client.account.deleteAccount({ body: payload, extraHeaders: await getAuthHeaders() })),
    getScanSummary: async (): Promise<AccountScanSummaryResponse> =>
      unwrapAuthed<AccountScanSummaryResponse>((headers) =>
        client.account.getScanSummary({ extraHeaders: headers })),
  },

  scan: {
    start: {
      mutate: async (payload: { limitEmails?: number; scan_mode?: 'full' | 'delta'; intent?: 'manual' | 'automatic' }) =>
        unwrapAuthed<any>((headers) => client.scan.startScan({ body: { scan_mode: payload.scan_mode || 'full', intent: payload.intent ?? 'manual' }, extraHeaders: headers })),
    },

    resetSample: {
      mutate: async () =>
        unwrapAuthed<any>((headers) => client.scan.resetSampleScan({ body: {}, extraHeaders: headers })),
    },
    
    pause: {
      mutate: async (payload: { job_id: string }) =>
        unwrap<any>(client.scan.pauseScan({ params: { id: payload.job_id }, body: {}, extraHeaders: await getAuthHeaders() })),
    },

    resume: {
      mutate: async (payload: { job_id: string }) =>
        unwrap<any>(client.scan.resumeScan({ params: { id: payload.job_id }, body: {}, extraHeaders: await getAuthHeaders() })),
    },

    cancel: {
      mutate: async (payload: { job_id: string }) =>
        unwrap<any>(client.scan.cancelScan({ params: { id: payload.job_id }, body: {}, extraHeaders: await getAuthHeaders() })),
    },

    current: {
      query: async () =>
        unwrapAuthed<any>((headers) => client.scan.getCurrentScan({ extraHeaders: headers })),
    },

    history: {
      query: async (params?: { limit?: number; offset?: number; status?: 'COMPLETED' }) =>
        unwrap<any>(client.scan.listScanHistory({ query: params || {}, extraHeaders: await getAuthHeaders() })),
    },

    getJob: {
      query: async (job_id: string) =>
        unwrap<any>(client.scan.getScanJob({ params: { id: job_id }, extraHeaders: await getAuthHeaders() })),
    },
  },

  preferences: {
    getAutomation: async () =>
      unwrap<any>(client.preferences.getAutomation({ extraHeaders: await getAuthHeaders() })),

    updateAutomation: async (mode: 'auto_apply' | 'classify_only') =>
      unwrap<any>(
        client.preferences.updateAutomation({
          body: { mode },
          extraHeaders: await getAuthHeaders(),
        }),
      ),

    recordConsent: async () =>
      unwrap<any>(
        client.preferences.recordConsent({
          body: undefined,
          extraHeaders: await getAuthHeaders(),
        }),
      ),

    getEmailPreferences: async () =>
      unwrap<any>(client.preferences.getEmailPreferences({ extraHeaders: await getAuthHeaders() })),

    setEmailConsent: async (
      purpose: EmailConsentPurpose,
      consent: boolean,
      source: EmailConsentSource,
      copyVersion: string,
    ) =>
      unwrap<any>(
        client.preferences.updateEmailPreference({
          body: {
            action: 'set_consent',
            purpose,
            consent,
            source,
            copy_version: copyVersion,
          },
          extraHeaders: await getAuthHeaders(),
        }),
      ),

    recordEmailPrompt: async (
      action: Extract<UpdateEmailPreferenceRequest, {
        action: 'record_impression' | 'dismiss_prompt'
      }>['action'],
      purpose: EmailConsentPurpose,
      source: EmailConsentSource,
      copyVersion: string,
    ) =>
      unwrap<any>(
        client.preferences.updateEmailPreference({
          body: {
            action,
            purpose,
            source,
            copy_version: copyVersion,
          },
          extraHeaders: await getAuthHeaders(),
        }),
      ),
  },

  stripe: {
    createCheckoutSession: async (payload: {
      priceId: 'monthly' | 'yearly';
      source?: 'cleanup-preview' | 'pricing' | 'upgrade' | 'homepage';
      referralId?: string;
      utmSource?: string;
    }) =>
      unwrap<any>(client.stripe.createCheckoutSession({ body: payload, extraHeaders: await getAuthHeaders() })),

    getSubscriptionStatus: async () =>
      unwrap<any>(client.stripe.getSubscriptionStatus({ extraHeaders: await getAuthHeaders() })),

    createBillingPortalSession: async (_payload?: Record<string, unknown>) =>
      unwrap<any>(client.stripe.createBillingPortalSession({ body: {}, extraHeaders: await getAuthHeaders() })),
  },

  feedbackOffer: {
    getCheckout: async (): Promise<FeedbackOfferCheckoutResponse> =>
      unwrap<FeedbackOfferCheckoutResponse>(
        client.feedbackOffer.getCheckout({
          body: undefined,
          extraHeaders: await getAuthHeaders(),
        }),
      ),
  },

  subscription: {
    // Server-derived tier + emails_limit. Prefer this over `stripe.getSubscriptionStatus`
    // when you need to know whether the user is Free or Pro - the stripe response does
    // not carry a `tier` field, only the raw Stripe `status`.
    getStatus: async () =>
      unwrap<any>(client.subscription.getStatus({ extraHeaders: await getAuthHeaders() })),
  },

  // Account-side MCP assistant-connection management (#525, epic #524).
  // Lists and unlinks the caller's assistant links. Never returns tokens.
  mcp: {
    listConnections: async (): Promise<ListMcpConnectionsResponse> =>
      unwrapAuthed<ListMcpConnectionsResponse>((headers) => client.mcp.listConnections({ extraHeaders: headers })),

    revokeConnection: async (id: string): Promise<RevokeMcpConnectionResponse> =>
      unwrapAuthed<RevokeMcpConnectionResponse>((headers) => client.mcp.revokeConnection({ params: { id }, body: {}, extraHeaders: headers })),
  },

  attribution: {
    record: async (payload: UserAttributionPayload) =>
      unwrap<any>(
        client.attribution.saveAttribution({
          body: payload,
          extraHeaders: await getAuthHeaders(),
        }),
      ),
    get: async () =>
      unwrap<any>(
        client.attribution.getAttribution({
          extraHeaders: await getAuthHeaders(),
        }),
      ),
  },
};